"use client";

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import ReportsGrid from './ReportsGrid';
import { fetchIndustries, fetchSubIndustries } from '@/utils/fetchReports';

interface ResearchTabsProps {
    industryKey: string;
    bannerTitle: string;
    segmentsContent?: React.ReactNode;
    insightsContent?: React.ReactNode;
    caseStudiesContent?: React.ReactNode;
    hideInsights?: boolean;
    initialReports?: any[];
    hideReports?: boolean;
}

const ResearchTabs: React.FC<ResearchTabsProps> = ({
    industryKey,
    bannerTitle,
    segmentsContent,
    insightsContent,
    caseStudiesContent,
    hideInsights = false,
    initialReports,
    hideReports = false
}) => {
    const searchParams = useSearchParams();
    const urlSubIndustryId = searchParams.get('sub_industry_id');

    const [activeTab, setActiveTab] = useState(hideReports ? 'segments' : 'reports');
    const [industryId, setIndustryId] = useState<string | null>(null);
    const [subIndustries, setSubIndustries] = useState<any[]>([]);
    const [selectedSubIndustryId, setSelectedSubIndustryId] = useState<string | null>(urlSubIndustryId);
    const displayTitle = bannerTitle.replace(" Coverage", "");

    // Fetch industry ID from industry key
    useEffect(() => {
        async function loadIndustryId() {
            const industries = await fetchIndustries();
            const industry = industries.find((ind: any) => ind.industry_key === industryKey);
            if (industry) {
                setIndustryId(industry.industry_id);
            }
        }
        loadIndustryId();
    }, [industryKey]);

    // Fetch sub-industries when industry ID is available
    useEffect(() => {
        async function loadSubIndustries() {
            if (!industryId) return;
            const subIndus = await fetchSubIndustries(industryId);
            setSubIndustries(subIndus);
        }
        loadSubIndustries();
    }, [industryId]);

    // Update selected sub-industry when URL parameter changes
    useEffect(() => {
        if (urlSubIndustryId) {
            setSelectedSubIndustryId(urlSubIndustryId);
        }
    }, [urlSubIndustryId]);

    return (
        <div className="container sub-ind">
            <div className="row">
                <div className="col-md-12">
                    <div className="nav nav-tabs">
                        {!hideReports && (
                            <div className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}> Reports </div>
                        )}
                        <div className={`tab-button ${activeTab === 'segments' ? 'active' : ''}`} onClick={() => setActiveTab('segments')}> <span>{displayTitle}</span> Segments </div>
                        {!hideInsights && (
                            <div className={`tab-button ${activeTab === 'insights' ? 'active' : ''}`} onClick={() => setActiveTab('insights')}> Industry Insights </div>
                        )}
                        {caseStudiesContent && (
                            <div className={`tab-button ${activeTab === 'case-studies' ? 'active' : ''}`} onClick={() => setActiveTab('case-studies')}> Case Studies </div>
                        )}
                    </div>

                    <div className="tab-content">
                        {activeTab === 'segments' && (
                            <div id="segments" className="tab-pane fade show active">
                                {segmentsContent || (
                                    <>
                                        <summary>{displayTitle}</summary>
                                        <p>Full segmentation data coming soon.</p>
                                    </>
                                )}
                            </div>
                        )}

                        {activeTab === 'reports' && !hideReports && (
                            <div id="reports" className="tab-pane fade show active">
                                <ReportsGrid
                                    industryKey={industryKey}
                                    initialReports={initialReports}
                                    subIndustryId={selectedSubIndustryId}
                                />
                            </div>
                        )}

                        {activeTab === 'insights' && !hideInsights && (
                            <div id="insights" className="tab-pane fade show active">
                                {insightsContent || (
                                    <>
                                        <h3>Industry Insights</h3>
                                        <p>Latest insights and trends in the {displayTitle} sector.</p>
                                    </>
                                )}
                            </div>
                        )}

                        {activeTab === 'case-studies' && caseStudiesContent && (
                            <div id="case-studies" className="tab-pane fade show active">
                                {caseStudiesContent}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResearchTabs;
